import { DemoSlide } from './demo-slide.js';
export class DemoTour {
  constructor(el){
    this.el = el;

    if(!this.el || this.el instanceof HTMLElement === false) return;

    this.id = el ? el.id : null;

    // Empty state to start
    this.state = {};
    this.resetDemoState();
    this.focusTrapEls = [];

    this.helperModal = {
      eyebrow: (this.el.dataset.helperModalEyebrow || '').trim(),
      heading: (this.el.dataset.helperModalHeading || '').trim(),
      sessionKey: 'demo_tour_helper_shown',
    };

    this.gating = {
      cookieName: 'bruker_demo_tour_gated',
      triggerSlideIndex: 3, // Gate when attempting to move from slide 3 to slide 4 (0-based)
      heading: (this.el.dataset.gatingHeading || 'Please provide additional information').trim(),
      content: (this.el.dataset.gatingContent || 'Please fill out the form below to continue viewing this demo').trim(),
      marketo: {
        scriptSrc: '//go.brukerspatialbiology.com/js/forms2/js/forms2.min.js',
        host: '//go.brukerspatialbiology.com/',
        munchkinId: '700-JYF-171',
        formId: 3023,
      },
    };

    this.state.gating = {
      unlocked: this.hasGatingCookie() || this.hasSkipGatingParam(),
      active: false,
      pendingSlideIndex: null,
      insertedSlide: null,
    };

    this.marketoScriptPromise = null;

    this.els = {
      demoIntro: this.el.querySelector('.demo-tour__path-intro'),
      slidesWrap: this.el.querySelector('.demo-tour__slides'),
      componentIntro: this.el.querySelector('.demo-tour__intro'),
      sidebar: this.el.querySelector('.demo-tour__sidebar'),
      demoOutro: this.el.querySelector('.demo-tour__path-outro'),
      helperModal: this.el.querySelector('.demo-tour__helper-modal'),
      helperBackdrop: this.el.querySelector('.demo-tour__helper-backdrop'),
    }

    let debouncer;
    window.addEventListener('resize', () => {
      clearTimeout(debouncer);
      debouncer = setTimeout(() => {
        if(this.state.slides && this.state.slides.length > 0){
          this.state.slides.forEach((slide)=>{
            if(slide.instance && typeof slide.instance.setUI === 'function') {
              slide.instance.setUI();
            }
          });
        }
      }, 50);
    });
  }

  resetDemoState() {
    this.state.demo = {
      id: null,
      title: null,
      slidesHtml: null,
      intro: null,
      outro: null,
    };
  }

  init(){
    if(!this.el) return;
    document.addEventListener('click', (e)=>{
      const a = e.target.closest('a') ? e.target.closest('a') : null;
      if(!a) return;
      const hash = a.hash;
      if(!hash) return;
      
      if(hash == '#' + this.id){
        e.preventDefault();
        this.open({ blurFocusedLink: true });
      }
    });

    // Click events for demo controls
    this.el.addEventListener('click',(e)=>{
      if(e.target.closest('.demo-tour__close')){ this.close(); }
      if(e.target.closest('.demo-tour__helper-got-it')){ this.closeHelperModal(); }
      if(e.target.closest('.demo-slide__home')){ this.setActiveScreenElement(this.els.componentIntro); }
      // Begin Demo from Intro
      if(e.target.closest('.demo-tour__path-intro-start-btn')){ e.preventDefault(); this.beginSlides(); }
      // Slide navigation
      if(e.target.closest('.demo-slide__prev')){ this.prevSlide(); }
      if(e.target.closest('.demo-slide__next')){ this.nextSlide(); }
      // Outro prev (desktop + mobile): go back to last slide
      if(
        e.target.closest('.demo-tour__path-outro-back-btn') ||
        e.target.closest('.demo-tour__outro-prev, .demo-tour__path-outro-prev-btn') ||
        e.target.closest('.demo-tour__path-outro-prev') ||
        e.target.closest('.demo-outro__prev') ||
        e.target.closest('[data-action="outro-prev"]') ||
        e.target.closest('[data-outro-prev]') ||
        (e.target.closest('[class*="outro-prev"]'))
      ){
        e.preventDefault();
        this.returnToLastSlideFromOutro();
      }
    });

    // Sidebar open/close
    this.el.addEventListener('click',(e)=>{
      if(this.isSidebarOpen && !e.target.closest('.demo-tour__sidebar')){ this.closeSidebar(); }
      else {
        if(e.target.closest('.demo-menu-toggle')){ this.openSidebar(); }
        if(e.target.closest('.demo-tour__sidebar-close')){ this.closeSidebar(); }
      }
    });

    // Keyboard accessibility: ESC to close, arrows to navigate active slides
    document.addEventListener('keydown',(e)=>{
      const isEditableTarget = e.target && e.target.closest('input, textarea, select, [contenteditable="true"]');
      if(isEditableTarget) return;

      if(e.key === 'Escape'){
        if(this.isSidebarOpen){ this.closeSidebar(); }
        if(this.isOpen()){ this.close(); }
        return;
      }

      const isSlidesActive = this.isOpen() && this.els.slidesWrap && this.els.slidesWrap.classList.contains('active');
      if(!isSlidesActive || this.isSidebarOpen || this.isHelperModalVisible()) return;

      if(e.key === 'ArrowLeft'){
        e.preventDefault();
        this.prevSlide();
      }

      if(e.key === 'ArrowRight'){
        e.preventDefault();
        this.nextSlide();
      }
    });

    // Select A Demo
    this.el.addEventListener('click',(e)=>{
      const demoSelectBtn = e.target.closest('.demo-tour__sidebar-link');
      if(!demoSelectBtn) return;
      e.preventDefault();
      const demoId = demoSelectBtn.dataset.demoId;
      if(!demoId) return;
      this.closeSidebar();
      this.loadDemo(demoId);
    });

    if(this.isMatchingComponentHash()){
      this.open({ blurFocusedLink: true });
      return;
    }

    const demoIdFromHash = this.checkUrlHashForDemoId();
    if(demoIdFromHash){
      this.open({ blurFocusedLink: true });
      this.loadDemo(demoIdFromHash);
    }
  }

  isOpen(){ return this.el.classList.contains('demo-tour--open'); }

  open(options = {}){
    const { blurFocusedLink = false } = options;

    this.el.style.opacity = '0';
    this.el.style.transform = 'translate(0, 20px)';
    this.el.setAttribute('aria-hidden','false');
    this.setActiveScreenElement(this.els.componentIntro);
    this.el.classList.add('demo-tour--open');
    this.previousFocus = document.activeElement;
    if(
      blurFocusedLink &&
      this.previousFocus &&
      this.previousFocus.matches('a, button') &&
      typeof this.previousFocus.blur === 'function'
    ){
      this.previousFocus.blur();
    }
    this.disableExternalFocusableElements();
    this.updateFocusabilityForCurrentState();

    if(blurFocusedLink){
      this.blurFocusedInteractiveElement();
      requestAnimationFrame(() => this.blurFocusedInteractiveElement());
      setTimeout(() => this.blurFocusedInteractiveElement(), 0);
    }

    setTimeout(() => {
      this.el.style.opacity = '1';
      this.el.style.transform = 'translate(0, 0)';
    }, 1);
  }

  blurFocusedInteractiveElement(){
    const activeEl = document.activeElement;
    if(!activeEl || !activeEl.matches('a, button') || typeof activeEl.blur !== 'function') return;
    activeEl.blur();
  }

  close(){
    this.el.style.opacity = '0';
    this.el.style.transform = 'translate(0, 20px)';
    this.closeGatingOverlay();
    if(this.previousFocus) this.previousFocus.focus();
    this.restoreExternalFocusableElements();
    this.el.setAttribute('aria-hidden','true');
    
    setTimeout(() => {
      this.el.classList.remove('demo-tour--open');
      this.el.style.opacity = '';
      this.el.style.transform = '';
    }, 500);
  }

  // Disable focusable elements outside the demo component
  disableExternalFocusableElements(){
    // console.log('Disabling external focusable elements');
    const allFocusableEls = Array.from(document.querySelectorAll('a,button,iframe,input,select,textarea,[tabindex]')).filter(el=>!el.hasAttribute('disabled'));
    this._outsideDemoTabindexes = new Map();
    allFocusableEls.forEach(el => {
      // console.log('element being checked:', el);

      // Skip elements that are inside the demo component
      if(this.el.contains(el)) return;
      
      const originalTabindex = el.getAttribute('tabindex');
      this._outsideDemoTabindexes.set(el, originalTabindex);
      el.setAttribute('tabindex', '-1');
      // console.log('element tabindex set to -1:', el);
    });
  }

  // Restore focusable elements outside the demo component
  restoreExternalFocusableElements(){
    if(this._outsideDemoTabindexes){
      this._outsideDemoTabindexes.forEach((originalTabindex, el) => {
        if(originalTabindex === null){
          el.removeAttribute('tabindex');
        } else {
          el.setAttribute('tabindex', originalTabindex);
        }
      });
      this._outsideDemoTabindexes = null;
    }
  }

  // Update focusability of elements based on visibility in current demo state
  updateFocusabilityForCurrentState(){
    const allDemoFocusable = Array.from(this.el.querySelectorAll('a,button,input,select,textarea,[tabindex]')).filter(el=>!el.hasAttribute('disabled'));
    
    // Get all parent screens/sections once
    const componentIntro = this.el.querySelector('.demo-tour__intro');
    const slidesWrap = this.el.querySelector('.demo-tour__slides');
    const demoIntro = this.el.querySelector('.demo-tour__path-intro');
    const demoOutro = this.el.querySelector('.demo-tour__path-outro');
    const sidebar = this.el.querySelector('.demo-tour__sidebar');
    
    allDemoFocusable.forEach(el => {
      let isVisible = false;
      
      // Check if element is in the sidebar
      if(sidebar && sidebar.contains(el)){
        // Sidebar elements are only visible if sidebar is open
        isVisible = this.isSidebarOpen;
      } else if(componentIntro && componentIntro.contains(el)){
        // Element is in intro screen
        isVisible = componentIntro.classList.contains('active');
      } else if(slidesWrap && slidesWrap.contains(el)){
        // Element is in slides screen
        isVisible = slidesWrap.classList.contains('active');
      } else if(demoIntro && demoIntro.contains(el)){
        // Element is in demo intro (shoulder) screen
        isVisible = demoIntro.classList.contains('active');
      } else if(demoOutro && demoOutro.contains(el)){
        // Element is in demo outro (shoulder) screen
        isVisible = demoOutro.classList.contains('active');
      } else {
        // Elements not in any main screen section (like close button, navigation)
        isVisible = true;
      }
      
      // Set tabindex based on visibility
      if(isVisible){
        el.setAttribute('tabindex', '0');
      } else {
        el.setAttribute('tabindex', '-1');
      }
    });
  }

  openSidebar(){
    this.isSidebarOpen = true;
    if(this.els.sidebar){ this.els.sidebar.classList.add('is-open'); this.el.classList.add('sidebar-open'); this.els.sidebar.setAttribute('aria-hidden', 'false'); }

    // Focus the first link in the sidebar
    const firstLink = this.els.sidebar.querySelector('a, button, [tabindex]:not([tabindex="-1"])');
    if(firstLink) firstLink.focus(); firstLink.blur();

    this.updateFocusabilityForCurrentState();
  }
  closeSidebar(){
    this.isSidebarOpen = false;
    if(this.els.sidebar){ this.els.sidebar.classList.remove('is-open'); this.el.classList.remove('sidebar-open'); this.els.sidebar.setAttribute('aria-hidden', 'true'); }

    this.updateFocusabilityForCurrentState();
  }

  setActiveScreenElement(newActiveEl){
    if(!newActiveEl) return;

    if(newActiveEl !== this.els.slidesWrap){
      this.closeGatingOverlay();
    }

    // For this.els.demoIntro, this.els.slidesWrap, this.els.demoOutro, this.els.componentIntro
    // remove the .active class and set aria-hidden to true
    [this.els.demoIntro, this.els.slidesWrap, this.els.demoOutro, this.els.componentIntro].forEach(el => {
      if (!el) return;
      el.classList.remove('active');
      el.setAttribute('aria-hidden', 'true');

      if(newActiveEl === el){
        el.classList.add('active');
        el.setAttribute('aria-hidden', 'false');

        // Set data-state attribute on main el
        const stateName = (el.getAttribute('data-state-name'));
        if(stateName){
          this.el.setAttribute('data-state', stateName);
        }
      }

      // Reset state.demo if returning to component intro
      if(newActiveEl === this.els.componentIntro){this.resetDemoState();}
    });

    this.updateFocusabilityForCurrentState();
  }

  setLoadingState(){
    this.isLoading = true;
    // Create a new element for a loading overlay
    const loadingOverlay = document.createElement('div');
    loadingOverlay.className = 'demo-tour__loading-overlay';
    loadingOverlay.setAttribute('aria-live','polite');
    loadingOverlay.innerHTML = `
      <div class="demo-tour__loading-spinner" role="status" aria-label="Loading">
        <span class="demo-tour__loading-dot"></span>
        <span class="demo-tour__loading-dot"></span>
        <span class="demo-tour__loading-dot"></span>
      </div>
    `;
    this.el.appendChild(loadingOverlay);
  }

  removeLoadingState() {
    this.isLoading = false;
    const loadingOverlay = this.el.querySelector('.demo-tour__loading-overlay');
    if (loadingOverlay) {
      this.el.removeChild(loadingOverlay);
    }
  }

  async loadDemo(demoId){
    this.setLoadingState();
    this.state.currentDemoId = demoId;

    try {
      const url = `/wp-admin/admin-ajax.php?action=get_demo_slides&postId=${encodeURIComponent(demoId)}`;
      const res = await fetch(url, { credentials: 'same-origin' });
      const json = await res.json();
      if(!json || json.success !== true){
        throw new Error('Failed to load demo data');
      }
      // Persist demo data for intro/slides/outro
      this.state.demo = {
        id: demoId,
        title: json.data && json.data.title ? json.data.title : '',
        slidesHtml: json.data && json.data.content ? json.data.content : '',
        intro: json.data && json.data.intro ? json.data.intro : '',
        outro: json.data && json.data.outro ? json.data.outro : '',
      };
      // console.log('Demo data loaded:', this.state.demo);

      // After data arrives, render Demo Intro
      await this.populatePathShoulderScreen('intro');
    } catch(err){
      // console.error('DemoTour loadDemo error:', err);
    }
  }

  async populatePathShoulderScreen(type = 'intro'){
    try{
      const isOutro = type === 'outro';
      const container = isOutro ? this.els.demoOutro : this.els.demoIntro;
      if(!this.state.demo || !this.state.demo?.[type] || !container) return;

      const data = this.state.demo?.[type] || {};
      const title = this.state.demo?.title ? this.state.demo.title : '';
      const screenTitle = data?.heading ? data.heading : title;
      const description = data?.description ? data.description : '';
      const mediaType = data?.media_type === 'video' ? 'video' : 'image';
      const imageUrl = data?.image ? data.image : '';
      const videoUrl = data?.video ? data.video : '';
      const ctaLabel = isOutro ? '' : (data?.cta_label ? data.cta_label : 'Start Demo');

      const headingEl = container.querySelector(`.demo-tour__path-${type}-heading`);
      const descEl = container.querySelector(`.demo-tour__path-${type}-description`);
      const imageWrapEl = container.querySelector(`.demo-tour__path-${type}-image-wrap`);
      const imageInnerWrapEl = container.querySelector(`.demo-tour__path-${type}-image-inner-wrap`);
      const ctaEl = isOutro ? null : container.querySelector(`.demo-tour__path-${type}-start-btn`);

      if(headingEl){ headingEl.innerHTML = this.formatRegisteredMarks(screenTitle); }
      if(descEl){ descEl.innerHTML = this.formatRegisteredMarks(description); }
      if(ctaEl){ ctaEl.textContent = this.escapeHtml(ctaLabel); }

      if(imageWrapEl){
        imageWrapEl.classList.toggle('is-video', mediaType === 'video' && !!videoUrl);
      }

      const mediaEl = this.syncPathShoulderMediaElement({
        type,
        mediaType,
        imageUrl,
        videoUrl,
        imageInnerWrapEl,
      });

      if(imageWrapEl && ((mediaType === 'video' && videoUrl) || (mediaType !== 'video' && imageUrl))){
        imageWrapEl.style.display = 'block';
      } else if(imageWrapEl){
        imageWrapEl.style.display = 'none';
      }

      // Update breadcrumb title
      const crumbTitle = this.el.querySelector('.demo-tour__breadcrumb-title');
      if(crumbTitle && title){ crumbTitle.textContent = title; }

      // Wait for the chosen media to load before switching
      if(mediaType === 'video' && mediaEl && videoUrl){
        await new Promise((resolve)=>{
          if(mediaEl.readyState >= 2){
            resolve();
            return;
          }

          const handleReady = () => {
            mediaEl.removeEventListener('loadeddata', handleReady);
            mediaEl.removeEventListener('error', handleReady);
            resolve();
          };

          mediaEl.addEventListener('loadeddata', handleReady, { once: true });
          mediaEl.addEventListener('error', handleReady, { once: true });
        });
      } else if(mediaType !== 'video' && mediaEl && imageUrl){
        await new Promise((resolve)=>{
          if(mediaEl.complete){
            resolve();
            return;
          }

          mediaEl.onload = () => { resolve(); };
          mediaEl.onerror = () => { resolve(); };
        });
      }

      // Show the screen
      this.setActiveScreenElement(container);
      if(mediaType === 'video' && mediaEl && videoUrl){
        mediaEl.play().catch(() => {});
      }
    } catch(err){
      console.error('DemoTour populatePathShoulderScreen error:', err);
    } finally {
      this.removeLoadingState();
    }
  }

  syncPathShoulderMediaElement({ type, mediaType, imageUrl, videoUrl, imageInnerWrapEl }){
    if(!imageInnerWrapEl) return null;

    const existingMediaEls = imageInnerWrapEl.querySelectorAll(`.demo-tour__path-${type}-image, .demo-tour__path-${type}-video`);
    existingMediaEls.forEach((mediaEl)=>{
      if(mediaEl.tagName === 'VIDEO'){
        mediaEl.pause();
        mediaEl.removeAttribute('src');
        mediaEl.load();
      }
      mediaEl.remove();
    });

    if(mediaType === 'video' && videoUrl){
      const videoEl = document.createElement('video');
      videoEl.className = `demo-tour__path-${type}-image demo-tour__path-shoulder-image demo-tour__path-${type}-video demo-tour__path-shoulder-video`;
      videoEl.muted = true;
      videoEl.autoplay = true;
      videoEl.playsInline = true;
      videoEl.loop = true;
      videoEl.setAttribute('muted', '');
      videoEl.setAttribute('autoplay', '');
      videoEl.setAttribute('playsinline', '');
      videoEl.setAttribute('loop', '');
      videoEl.src = videoUrl;
      imageInnerWrapEl.appendChild(videoEl);
      videoEl.load();
      return videoEl;
    }

    if(imageUrl){
      const imgEl = document.createElement('img');
      imgEl.alt = '';
      imgEl.className = `demo-tour__path-${type}-image demo-tour__path-shoulder-image`;
      imgEl.src = imageUrl;
      imageInnerWrapEl.appendChild(imgEl);
      return imgEl;
    }

    return null;
  }

  updateSlideVisibility(){
    this.state.slides.forEach((slide, idx)=>{
      const slideIsActive = (idx === this.state.currentSlideIndex);
      if(slideIsActive){
        slide.classList.add('active');
        slide.setAttribute('aria-hidden','false');
        if(slide.instance && typeof slide.instance.setUI === 'function'){
          slide.instance.setUI(true); // ensure sizing
        }
        if(slide.instance && typeof slide.instance.resumeAutoplayVideos === 'function'){
          slide.instance.resumeAutoplayVideos();
        }
      } else if(slide.classList.contains('active')){ 
        slide.classList.remove('active');
        slide.setAttribute('aria-hidden','true');
      }
    });

    this.updateFocusabilityForCurrentState();
  }

  refreshSlidesState(){
    const slidesWrap = this.els.slidesWrap || this.el.querySelector('.demo-tour__slides');
    if(!slidesWrap) return [];

    this.state.slides = Array.from(slidesWrap.querySelectorAll('.demo-slide'));
    return this.state.slides;
  }

  stopSlideMedia(slide){
    if(!slide) return;

    const videos = slide.querySelectorAll('video');
    if(!videos || videos.length === 0) return;

    videos.forEach((video)=>{
      video.pause();
      try {
        video.currentTime = 0;
      } catch(err) {
        // Ignore media reset errors for streams or restricted sources.
      }
    });
  }

  isHelperModalVisible(){
    return this.els.helperModal && this.els.helperModal.classList.contains('is-active');
  }

  prevSlide(){
    if(this.isHelperModalVisible()) return;
    this.closeGatingOverlay();

    const currentSlide = this.state.slides?.[this.state.currentSlideIndex];
    this.stopSlideMedia(currentSlide);

    if(this.state.currentSlideIndex <= 0){
      // back to demo intro
      if(this.els.demoIntro){ this.setActiveScreenElement(this.els.demoIntro); }
      return;
    }
    this.state.currentSlideIndex -= 1; this.updateSlideVisibility();
  }
  nextSlide(){
    if(this.isHelperModalVisible()) return;
    if(this.state.gating?.active) return;

    const currentSlide = this.state.slides?.[this.state.currentSlideIndex];
    this.stopSlideMedia(currentSlide);

    const nextIndex = this.state.currentSlideIndex + 1;
    if(this.shouldShowGateForNextIndex(nextIndex)){
      this.openGatingOverlay(nextIndex);
      return;
    }

    if(this.state.currentSlideIndex >= this.state.slides.length-1){
      // go to outro screen
      // Populate and show outro screen
      this.populatePathShoulderScreen('outro');
      return;
    }
    this.state.currentSlideIndex += 1; this.updateSlideVisibility();
  }

  isMatchingComponentHash(){
    const hash = window.location.hash;
    if(!hash || !hash.startsWith('#') || !this.id) return false;

    let normalizedHash = hash.substring(1).trim();
    if(!normalizedHash) return false;

    try {
      normalizedHash = decodeURIComponent(normalizedHash);
    } catch(err) {
      return false;
    }

    return normalizedHash === String(this.id);
  }

  // Check url hash for a demo ID to load on page load
  checkUrlHashForDemoId(){
    const hash = window.location.hash;
    if(!hash || !hash.startsWith('#')) return null;

    let demoId = hash.substring(1).trim();
    if(!demoId) return null;

    try {
      demoId = decodeURIComponent(demoId);
    } catch(err) {
      return null;
    }

    // Only deep-link when the hash matches a real demo ID in this component.
    const matchingSidebarLink = Array.from(
      this.el.querySelectorAll('.demo-tour__sidebar-link[data-demo-id]')
    ).find((linkEl) => String(linkEl.dataset.demoId) === demoId);

    return matchingSidebarLink ? demoId : null;
  }

  shouldShowGateForNextIndex(nextIndex){
    if(!this.state.slides || this.state.slides.length === 0) return false;
    if(this.state.gating.unlocked) return false;
    if(nextIndex >= this.state.slides.length) return false;
    return nextIndex >= this.gating.triggerSlideIndex;
  }

  hasGatingCookie(){
    const escapedName = this.gating.cookieName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(?:^|; )${escapedName}=1(?:;|$)`).test(document.cookie);
  }

  hasSkipGatingParam(){
    try {
      const params = new URLSearchParams(window.location.search);
      return params.has('skipGating');
    } catch(err) {
      return false;
    }
  }

  setGatingCookie(days = 365){
    const maxAge = Math.max(1, Math.floor(days * 24 * 60 * 60));
    const secureFlag = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${this.gating.cookieName}=1; Max-Age=${maxAge}; Path=/; SameSite=Lax${secureFlag}`;
  }

  ensureMarketoScriptLoaded(){
    if(window.MktoForms2) return Promise.resolve();
    if(this.marketoScriptPromise) return this.marketoScriptPromise;

    this.marketoScriptPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${this.gating.marketo.scriptSrc}"]`);
      if(existing){
        if(window.MktoForms2){
          resolve();
          return;
        }

        if(existing.dataset.loaded === '1'){
          resolve();
          return;
        }

        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('Failed to load Marketo script')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = this.gating.marketo.scriptSrc;
      script.async = true;
      script.onload = () => {
        script.dataset.loaded = '1';
        resolve();
      };
      script.onerror = () => reject(new Error('Failed to load Marketo script'));
      document.head.appendChild(script);
    });

    return this.marketoScriptPromise;
  }

  openGatingOverlay(nextSlideIndex){
    const currentSlide = this.state.slides?.[this.state.currentSlideIndex];
    if(!currentSlide) return;

    this.closeGatingOverlay();

    const hasCopy = Boolean(this.gating.heading || this.gating.content);
    const gateSlide = document.createElement('div');
    gateSlide.className = 'demo-slide demo-tour__slide demo-tour__gating';
    gateSlide.setAttribute('aria-hidden', 'true');
    gateSlide.innerHTML = `
      <div class="demo-tour__gating-inner${hasCopy ? ' has-copy' : ''}">
        ${hasCopy ? `
          <div class="demo-tour__gating-copy">
            <span class="demo-tour__gating-logo">
              <svg xmlns="http://www.w3.org/2000/svg" width="144" height="77" viewBox="0 0 144 77" fill="none">
                <g clip-path="url(#clip0_12328_1040)">
                  <mask id="mask0_12328_1040" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="0" y="0" width="144" height="77">
                    <path d="M144 0H0V77H144V0Z" fill="white"/>
                  </mask>
                  <g mask="url(#mask0_12328_1040)">
                    <mask id="mask1_12328_1040" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="0" y="0" width="144" height="77">
                      <path d="M144 0H0V77H144V0Z" fill="white"/>
                    </mask>
                    <g mask="url(#mask1_12328_1040)">
                      <path d="M0.174316 46.9389H17.3588C19.2229 46.9389 20.3102 46.025 20.5432 45.7722L20.8733 45.3833C21.0675 45.1306 21.2229 44.8583 21.3782 44.5861C21.8054 43.7111 22.019 42.7389 21.9607 41.7667C21.9219 40.5611 21.7277 39.8222 21.4947 39.3167C21.3976 39.1028 21.2811 38.9278 21.1452 38.7333L20.8345 38.3639C20.2714 37.6833 18.9898 37.1972 18.9898 37.1972C19.4753 37.0222 19.9219 36.7305 20.3102 36.3805C20.9898 35.6028 21.3588 34.5917 21.3199 33.5611C21.2811 32.4722 21.0675 31.6556 20.3685 30.8389C19.6695 30.0417 18.019 30.0417 18.019 30.0417H0.174316V46.9194V46.9389ZM13.2034 34.4361C13.3976 34.4361 13.5918 34.5333 13.7083 34.6889C13.8636 34.9222 13.9413 35.1944 13.9219 35.4861C13.9219 35.7583 13.8636 36.0111 13.7083 36.2444C13.6306 36.3805 13.4753 36.4778 13.3005 36.4778H7.45587V34.4361H13.2034ZM14.2908 40.25C14.5044 40.25 14.6986 40.3472 14.8345 40.5222C14.9122 40.6583 14.9898 40.8139 15.0093 40.9694V41.2611C15.0093 41.5333 14.9316 41.8055 14.7763 42.0583C14.6986 42.175 14.5627 42.2528 14.4267 42.2528H7.45587V40.25H14.2908ZM25.6306 46.9389H32.8151V40.5222H35.4559L39.3199 46.9389H47.2229L42.9316 39.8806C42.9316 39.8806 44.485 39.7056 45.4753 38.6944C46.4656 37.6833 46.8345 36.3805 46.8733 34.9222C46.9122 33.4639 46.2714 31.9861 45.553 31.3639C44.8539 30.7222 43.9607 30.1583 41.883 30.0611H25.6306V46.9583V46.9389ZM32.8151 36.4389V34.4361H38.6209C38.8151 34.4361 39.0093 34.5528 39.1063 34.7278C39.2423 34.9417 39.3005 35.2139 39.3005 35.4667C39.3005 35.7194 39.2423 35.9722 39.1063 36.2056C38.9898 36.3417 38.8345 36.4194 38.6597 36.4583H32.8151V36.4389ZM50.1355 30.0417V40.5222C50.0966 41.5917 50.3102 42.6417 50.7763 43.5944C50.9122 43.8472 51.0675 44.1 51.2423 44.3139L51.5141 44.5861C51.6306 44.7028 52.4267 45.5389 54.252 46.1806C56.5432 46.9778 58.0772 47.1139 60.2714 47.1333C61.9996 47.1333 63.7083 46.8806 65.3782 46.4139C66.5432 46.1028 67.6306 45.5194 68.5044 44.6833C70.0578 43.2639 70.0384 40.8139 70.0384 40.8139V30.0222H63.2034C63.2034 30.0222 63.2423 39.6667 63.2034 40.5028C63.2034 40.7944 63.1258 41.0861 62.9704 41.3389L62.6986 41.65C62.6015 41.7861 61.9219 42.3694 60.4073 42.3694C58.8928 42.3694 58.3685 42 57.883 41.6306C57.3976 41.2611 57.3782 40.3083 57.3782 40.3083V30.0417H50.1161H50.1355ZM74.5432 46.9583H81.3394V42.0583L83.65 40.2306L88.0772 46.9583H96.6015L89.1452 36.05L95.9025 30.0417H86.7568L81.3394 35.525V30.0417H74.5432V46.9583ZM98.718 46.9H118.388V42.2528H105.922V40.4056H115.456V36.2639H105.922V34.4361H118.388V30.0222H98.718V46.9ZM122.33 46.9389H129.514V40.5222H132.155L136.019 46.9389H143.922L139.611 39.8806C139.611 39.8806 141.184 39.7056 142.155 38.6944C143.145 37.6833 143.514 36.3805 143.553 34.9222C143.611 33.4639 142.951 31.9861 142.233 31.3639C141.534 30.7222 140.64 30.1583 138.563 30.0611H122.31V46.9583L122.33 46.9389ZM129.495 36.4583V34.4944H135.301C135.495 34.4944 135.689 34.6111 135.786 34.7667C135.922 34.9805 135.98 35.2333 135.98 35.4861C135.98 35.7389 135.922 35.9917 135.786 36.2056C135.669 36.3417 135.514 36.4194 135.339 36.4583H129.495Z" fill="white"/>
                      <path d="M34.9694 28.8548C34.7558 28.4465 34.484 28.0576 34.2121 27.7076L32.6199 25.5493C31.1053 23.5271 29.7073 21.4271 28.4257 19.2493C25.6879 14.5437 23.7461 9.27428 26.6587 6.31873C30.2704 2.77984 36.5811 2.44928 44.0762 4.17984L44.7752 4.35484V4.56873C44.7752 6.96039 46.717 8.90484 49.1053 8.92428C50.5422 8.92428 51.9014 8.20484 52.717 6.99928L52.7946 6.88262L54.95 7.71873C59.6878 9.68262 64.3092 11.9382 68.7946 14.4659L70.5228 15.4576L69.3383 16.2159C63.4548 20.1048 57.7849 24.3243 52.3675 28.8548H53.3383L55.4937 28.8743H58.6393C61.2995 26.2687 66.9112 21.9909 73.2412 18.0243L74.0374 17.5382L75.6102 18.5104C80.6199 21.6604 85.4548 25.1021 90.0568 28.8354H91.8626L93.4742 28.8548H93.5908C92.1733 27.9021 90.6781 26.7548 88.8917 25.3937L86.0179 23.1965L82.6199 20.6882C80.7752 19.3659 78.6781 17.9465 76.2898 16.4298L76.0762 16.2937L77.5519 15.4382C81.2024 13.3771 84.9306 11.5493 88.348 10.3048C99.6878 5.36595 114.969 2.74095 119.435 7.83539C124.678 13.8048 115.552 25.7826 113.183 28.8354H115.999C119.047 24.1882 123.299 17.6159 123.086 11.6465C123.028 10.3048 122.639 2.04095 110.387 2.04095C101.047 2.04095 89.7461 3.81039 73.649 13.4743L72.5422 14.1548L71.0859 13.2993C68.251 11.6659 65.0665 9.95484 61.4548 8.18539C59.5908 7.29095 57.7073 6.47428 55.8432 5.73539L53.4743 4.86039V4.54928C53.4743 2.15761 51.5519 0.21317 49.1442 0.193726C47.5519 0.193726 46.0762 1.06873 45.3189 2.46873L44.8723 2.3715C36.0956 0.465948 28.5616 0.932615 24.9888 4.45206C17.8044 11.6659 30.5616 28.8548 30.5616 28.8548H35.0082H34.9694Z" fill="white"/>
                      <mask id="mask2_12328_1040" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="0" y="0" width="144" height="77">
                        <path d="M143.902 0.175049H0.174316V76.9028H143.902V0.175049Z" fill="white"/>
                      </mask>
                      <g mask="url(#mask2_12328_1040)">
                        <path d="M115.981 48.2222H113.165C115.534 51.2944 124.66 63.2722 119.418 69.2416C114.952 74.3361 99.67 71.7111 88.3302 66.7722C84.9128 65.5278 81.1846 63.7 77.5341 61.6389L76.0584 60.7833L76.272 60.6472C78.6603 59.1305 80.7574 57.7111 82.6021 56.3889L86.0001 53.9L88.8739 51.7028C90.6409 50.3416 92.1555 49.175 93.573 48.2611H93.4565L91.8448 48.2416H90.039C85.437 51.9944 80.6021 55.4361 75.5924 58.5861L74.0196 59.5583L73.2234 59.0722C66.8739 55.1055 61.2817 50.8278 58.6215 48.2416H52.3497C57.7671 52.7916 63.4176 57.0111 69.3205 60.9L70.505 61.6583L68.7768 62.65C64.3108 65.1778 59.6895 67.4333 54.9322 69.3972L52.7768 70.2333L52.7186 70.1166C51.3788 68.1333 48.6798 67.6083 46.6798 68.95C45.4759 69.7666 44.7574 71.1083 44.7574 72.5472V72.7611L44.0584 72.9361C36.5438 74.6666 30.2526 74.3361 26.6409 70.7972C23.7283 67.8416 25.67 62.5722 28.4079 57.8666C29.6895 55.7083 31.0875 53.6083 32.6021 51.5666L34.1943 49.4083C34.4856 49.0389 34.738 48.65 34.9516 48.2416H30.505C30.505 48.2416 17.7477 65.4305 24.9322 72.625C28.505 76.1444 36.039 76.6111 44.8157 74.7055L45.2623 74.5889C46.4079 76.7083 49.0487 77.4861 51.1458 76.3389C52.5438 75.5805 53.4176 74.1028 53.4176 72.5083V72.1972L55.7671 71.3222C57.6312 70.5833 59.5147 69.7666 61.3788 68.8722C64.9904 67.0833 68.1749 65.3722 71.0098 63.7583L72.4662 62.9028L73.5729 63.5833C89.67 73.2472 100.971 75.0166 110.311 75.0166C122.583 75.0166 122.971 66.7528 123.029 65.4111C123.243 59.4416 118.99 52.8694 115.942 48.2028L115.981 48.2222Z" fill="white"/>
                      </g>
                    </g>
                  </g>
                </g>
                <defs>
                  <clipPath id="clip0_12328_1040">
                    <rect width="144" height="77" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
            </span>
            <div>
              ${this.gating.heading ? `<h4 class="demo-tour__gating-heading">${this.formatRegisteredMarks(this.gating.heading)}</h4>` : ''}
              ${this.gating.content ? `<p class="demo-tour__gating-description">${this.formatRegisteredMarks(this.gating.content)}</p>` : ''}
            </div>
            <button type="button" class="demo-slide__prev demo-tour__gating-prev">
              <svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 38 38" fill="none">
                <rect x="0.5" y="0.5" width="37" height="37" rx="18.5" stroke="#3B82F7"/>
                <path d="M15.1915 11.2898C15.3322 11.1159 15.5375 11.011 15.7571 11.0008C15.9767 10.9906 16.1903 11.076 16.3457 11.2361C16.5012 11.3961 16.5841 11.6162 16.5742 11.8424C16.5642 12.0685 16.4624 12.28 16.2936 12.425L10.6703 18.2234L29.2569 18.2234C29.5269 18.2381 29.7706 18.395 29.9017 18.6388C30.0328 18.8824 30.0328 19.1784 29.9017 19.4222C29.7706 19.6658 29.5269 19.8228 29.2569 19.8376L10.6703 19.8376L16.3025 25.6292C16.4443 25.7821 16.5233 25.9854 16.5233 26.1967C16.5233 26.4082 16.4442 26.6114 16.3025 26.7643C16.1565 26.9152 15.9582 27 15.7514 27C15.5446 27 15.3463 26.9152 15.2003 26.7643L8.23444 19.5901C8.08468 19.4416 8 19.2367 8 19.0225C8 18.8083 8.08467 18.6035 8.23444 18.4549L15.1915 11.2898Z" fill="#3B82F7"/>
              </svg>
              <span>Prev</span>
            </button>
          </div>
        ` : ''}
        <div class="demo-tour__gating-form-wrap">
          <form id="mktoForm_${this.gating.marketo.formId}"></form>
        </div>
      </div>
    `;

    currentSlide.insertAdjacentElement('afterend', gateSlide);
    const slides = this.refreshSlidesState();
    const gateSlideIndex = slides.indexOf(gateSlide);
    if(gateSlideIndex < 0) return;

    this.state.gating.active = true;
    this.state.gating.pendingSlideIndex = nextSlideIndex;
    this.state.gating.insertedSlide = gateSlide;
    this.state.currentSlideIndex = gateSlideIndex;
    this.updateSlideVisibility();

    this.ensureMarketoScriptLoaded()
      .then(() => {
        if(!window.MktoForms2 || !gateSlide.isConnected) return;

        window.MktoForms2.loadForm(
          this.gating.marketo.host,
          this.gating.marketo.munchkinId,
          this.gating.marketo.formId,
          (form) => {
            form.onSuccess(() => {
              const pendingIndex = this.state.gating.pendingSlideIndex;
              this.setGatingCookie();
              this.state.gating.unlocked = true;
              this.closeGatingOverlay();

              if(Number.isInteger(pendingIndex)){
                this.state.currentSlideIndex = pendingIndex;
                this.updateSlideVisibility();
              }

              return false;
            });
          }
        );
      })
      .catch(() => {
        // If Marketo cannot load, fail gracefully by removing the gate.
        const fallbackIndex = Math.max(0, this.state.currentSlideIndex - 1);
        this.closeGatingOverlay();
        this.state.currentSlideIndex = fallbackIndex;
        this.updateSlideVisibility();
      });
  }

  closeGatingOverlay(){
    const gateSlide = this.state.gating.insertedSlide;
    if(gateSlide && gateSlide.parentNode){
      gateSlide.parentNode.removeChild(gateSlide);
      this.refreshSlidesState();
    }

    this.state.gating.active = false;
    this.state.gating.pendingSlideIndex = null;
    this.state.gating.insertedSlide = null;
  }

  // New: beginSlides switches from Demo Intro to Slides view
  async beginSlides(){
    const demoId = this.state.currentDemoId;
    if(!demoId) return;
    // Switch from demo intro to slides container
    if(this.els.demoIntro){ this.els.demoIntro.classList.remove('active'); this.els.demoIntro.setAttribute('aria-hidden','true'); }

    // Fetch ACF block content for the demo via AJAX
    const url = `/wp-admin/admin-ajax.php?action=get_demo_slides&postId=${encodeURIComponent(demoId)}`;
    const res = await fetch(url, {credentials:'same-origin'});
    const json = await res.json();
    if(!json.success) return;
    const slidesHtml = json.data.content;
    const slidesWrap = this.el.querySelector('.demo-tour__slides');
    if(slidesWrap){
      slidesWrap.innerHTML = slidesHtml;
      this.refreshSlidesState();
      this.state.currentSlideIndex = 0;

      // Init Slides with Extended Demo Slide Class
      this.state.slides.forEach((slide)=>{
        slide.instance = new DemoSlide(slide);
        slide.instance.init();
      });

      // Inject slide counts into each slide's meta wrapper
      this.updateSlideCounts();
      this.updateSlideVisibility();
      // Show slides view
      this.setActiveScreenElement(slidesWrap);

      if(!sessionStorage.getItem(this.helperModal.sessionKey)){
        this.openHelperModal();
      }
    }
  }

  openHelperModal(){
    if(!this.els.helperModal || !this.els.helperBackdrop) return;

    const eyebrowEl = this.els.helperModal.querySelector('.demo-tour__helper-eyebrow');
    const headingEl = this.els.helperModal.querySelector('.demo-tour__helper-heading');

    if(eyebrowEl){ eyebrowEl.textContent = this.helperModal.eyebrow; }
    if(headingEl){ headingEl.textContent = this.helperModal.heading; }

    this.els.helperBackdrop.classList.add('is-active');
    this.els.helperBackdrop.setAttribute('aria-hidden', 'false');
    this.els.helperModal.classList.add('is-active');
    this.els.helperModal.setAttribute('aria-hidden', 'false');

    // Close when clicking directly on the backdrop
    this._helperBackdropHandler = (e) => {
      if(e.target === this.els.helperBackdrop) this.closeHelperModal();
    };
    this.els.helperBackdrop.addEventListener('click', this._helperBackdropHandler);

    // Set only "Got it" button as focusable, disable all others in demo
    const gotItBtn = this.els.helperModal.querySelector('.demo-tour__helper-got-it');
    if(gotItBtn){
      const allDemoFocusable = Array.from(this.el.querySelectorAll('a,button,input,select,textarea,[tabindex]')).filter(el=>!el.hasAttribute('disabled'));
      this._helperModalPreviousTabindexes = new Map();
      allDemoFocusable.forEach(el => {
        if(el !== gotItBtn){
          const original = el.getAttribute('tabindex');
          this._helperModalPreviousTabindexes.set(el, original);
          el.setAttribute('tabindex', '-1');
        } else {
          el.setAttribute('tabindex', '0');
        }
      });
      gotItBtn.focus();
    }
  }

  closeHelperModal(){
    if(!this.els.helperModal || !this.els.helperBackdrop) return;

    this.els.helperBackdrop.classList.remove('is-active');
    this.els.helperBackdrop.setAttribute('aria-hidden', 'true');
    this.els.helperModal.classList.remove('is-active');
    this.els.helperModal.setAttribute('aria-hidden', 'true');

    if(this._helperBackdropHandler){
      this.els.helperBackdrop.removeEventListener('click', this._helperBackdropHandler);
      this._helperBackdropHandler = null;
    }

    // Restore focusability based on current state
    if(this._helperModalPreviousTabindexes){
      this._helperModalPreviousTabindexes.forEach((original, el) => {
        if(original === null){
          el.removeAttribute('tabindex');
        } else {
          el.setAttribute('tabindex', original);
        }
      });
      this._helperModalPreviousTabindexes = null;
    }

    this.updateFocusabilityForCurrentState();

    try { sessionStorage.setItem(this.helperModal.sessionKey, '1'); } catch(e) { /* private browsing */ }
  }

  // Add "Slide X of Y" into the slide meta eyebrow wrapper
  updateSlideCounts(){
    const total = this.state.slides ? this.state.slides.length : 0;
    if(!total) return;
    this.state.slides.forEach((slide, idx)=>{
      const meta = slide.querySelector('.demo-slide__meta, .demo-tour__slide-meta');
      if(!meta) return;
      // Remove any existing count to avoid duplicates
      const existing = meta.querySelector('.demo-slide__count');
      if(existing) existing.remove();
      const countEl = document.createElement('span');
      countEl.className = 'demo-slide__count';
      countEl.textContent = `${idx+1} / ${total}`;
      // Append after eyebrow if present
      const eyebrow = meta.querySelector('.demo-slide__eyebrow');
      if(eyebrow && eyebrow.parentNode === meta){
        eyebrow.insertAdjacentElement('afterend', countEl);
      } else {
        meta.appendChild(countEl);
      }
    });
  }

  // From Outro back to last slide
  returnToLastSlideFromOutro(){
    const slidesWrap = this.el.querySelector('.demo-tour__slides');
    if(!slidesWrap || !this.state.slides || this.state.slides.length === 0) return;
    this.state.currentSlideIndex = this.state.slides.length - 1;
    this.updateSlideVisibility();
    this.setActiveScreenElement(slidesWrap);
  }

  // Utils
  escapeHtml(str){
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  formatRegisteredMarks(str){
    const normalized = String(str).replace(/&(reg|#174|#x0*ae);/gi, '®');
    return this.escapeHtml(normalized).replace(/®/g, '<sup>®</sup>');
  }
}

export function initDemoTour(){
  const tours = document.querySelectorAll('.demo-tour');
  if(!tours || tours.length === 0) return;
  tours.forEach(tourEl=>{
    const tour = new DemoTour(tourEl);
    tour.init();
  });
}
